import { PageHero, SectionHeader } from "@/components/Section";
import { church } from "@/config/church";
import useReveal from "@/hooks/useReveal";
import { Mail, ArrowRight } from "lucide-react";
import StockPhotoNotice from "@/components/StockPhotoNotice";

const leaders = [
  {
    name: church.pastor.name,
    role: "Senior Pastor",
    image: church.pastor.image,
    bio:
      "Called to preach the Word and shepherd God's people, Pastor Little leads with humility, biblical conviction, and a passion for revival in Memphis.",
    featured: true,
  },
  {
    name: "First Lady of the Church",
    role: "First Lady",
    image: "https://images.pexels.com/photos/9577192/pexels-photo-9577192.jpeg?auto=compress&cs=tinysrgb&w=900",
    bio: "Serving alongside the pastor with grace, prayer, and love for the women and families of the congregation.",
  },
  {
    name: "Executive Pastor",
    role: "Executive Pastor",
    image: "https://images.pexels.com/photos/13172188/pexels-photo-13172188.jpeg?auto=compress&cs=tinysrgb&w=900",
    bio: "Providing operational leadership so the ministry of the Word and prayer can flourish across every ministry.",
  },
  {
    name: "Minister of Music",
    role: "Minister of Music",
    image: "https://images.pexels.com/photos/7520344/pexels-photo-7520344.jpeg?auto=compress&cs=tinysrgb&w=900",
    bio: "Leading the congregation into Spirit-filled worship rooted in Scripture and sound doctrine.",
  },
  {
    name: "Youth Pastor",
    role: "Youth Pastor",
    image: "https://images.pexels.com/photos/6284493/pexels-photo-6284493.jpeg?auto=compress&cs=tinysrgb&w=900",
    bio: "Discipling the next generation to know Christ deeply and live boldly for Him in every arena.",
  },
  {
    name: "Children's Director",
    role: "Children's Director",
    image: "https://images.pexels.com/photos/9577018/pexels-photo-9577018.jpeg?auto=compress&cs=tinysrgb&w=900",
    bio: "Creating safe, joyful, gospel-rich environments where our youngest disciples fall in love with Jesus.",
  },
  {
    name: "Deacon Board Chair",
    role: "Deacon Board",
    image: "https://images.pexels.com/photos/7219817/pexels-photo-7219817.jpeg?auto=compress&cs=tinysrgb&w=900",
    bio: "Serving the practical needs of the church family with wisdom, integrity, and Christlike compassion.",
  },
  {
    name: "Missions Director",
    role: "Missions & Outreach",
    image: "https://images.pexels.com/photos/6275774/pexels-photo-6275774.jpeg?auto=compress&cs=tinysrgb&w=900",
    bio: "Mobilizing our congregation to serve Memphis and take the gospel to the nations.",
  },
];

export default function Leadership() {
  useReveal();
  return (
    <div data-testid="leadership-page">
      <PageHero
        eyebrow="Leadership"
        title="Servant leaders after God's own heart."
        subtitle="Meet the pastors, ministers, and lay leaders who steward the vision and shepherd the flock at New Philadelphia."
        image="https://images.pexels.com/photos/7219817/pexels-photo-7219817.jpeg?auto=compress&cs=tinysrgb&w=2000"
      />

      {/* Featured Pastor */}
      <section className="bg-white py-24">
        <div className="container-npbc grid lg:grid-cols-[1fr_1.4fr] gap-14 items-center">
          <div data-reveal className="mx-auto lg:mx-0 w-full max-w-[340px]">
            <div className="relative">
              <div className="absolute -inset-2 bg-gradient-to-br from-npbc-red/20 via-npbc-black/10 to-npbc-gold/20 rounded-2xl -rotate-1" />
              <div className="relative rounded-2xl overflow-hidden aspect-[4/5] bg-white shadow-[0_20px_50px_-25px_rgba(0,0,0,0.4)]">
                <img
                  src={church.pastor.image}
                  alt={church.pastor.name}
                  className="h-full w-full object-cover object-top"
                  loading="lazy"
                />
              </div>
              <div className="mt-4 text-center">
                <div className="text-[10px] font-semibold uppercase tracking-[0.28em] text-npbc-red">
                  Senior Pastor
                </div>
                <div className="heading-serif text-xl mt-1 text-npbc-black">
                  {church.pastor.name}
                </div>
              </div>
            </div>
          </div>
          <div data-reveal>
            <div className="eyebrow">Servant of the Lord</div>
            <h2 className="mt-3 heading-serif text-5xl md:text-6xl leading-[1.02]">
              A shepherd for this house.
            </h2>
            <p className="mt-6 text-npbc-gray leading-relaxed text-lg">{church.pastor.bio}</p>
            <blockquote className="mt-6 border-l-2 border-npbc-gold pl-5 italic text-npbc-black/80">
              "It is not about the pastor — it's about the Chief Shepherd and His flock. I am simply a servant."
            </blockquote>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href={`mailto:${church.email}`} className="btn-ghost-dark">
                <Mail className="h-4 w-4" /> Contact the Pastor
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Continuing the Legacy — three ministers photo */}
      <section className="bg-white py-24">
        <div className="container-npbc grid gap-14 lg:grid-cols-[1.1fr_1fr] items-center">
          <div data-reveal>
            <div className="eyebrow">Continuing the Legacy</div>
            <h2 className="mt-3 heading-serif text-5xl leading-[1.05]">
              Servants standing shoulder to shoulder.
            </h2>
            <p className="mt-6 text-npbc-gray leading-relaxed text-[17px]">
              A living picture of what New Philadelphia represents — pastors, preachers,
              and servants of the Lord united in the ministry of the Word. Every generation
              received the torch of faith from those who came before, and today they stand
              together to carry it forward.
            </p>
            <blockquote className="mt-6 border-l-2 border-npbc-gold pl-5 italic text-npbc-black/80 max-w-lg">
              "One plants, another waters, but God gives the increase." — 1 Corinthians 3:6
            </blockquote>
          </div>
          <div data-reveal className="relative">
            <div className="absolute -inset-3 rounded-3xl bg-gradient-to-br from-npbc-red/10 to-npbc-gold/20 -rotate-1" />
            <img
              src="https://customer-assets-eiarnc6j.emergentagent.net/job_strange-cartwright-8/artifacts/ers62mij_IMG_5591.jpg"
              alt="Pastor Darren M. Little with fellow ministers at New Philadelphia Missionary Baptist Church"
              className="relative rounded-3xl w-full h-[520px] object-cover object-top shadow-2xl"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      <section className="bg-npbc-cream py-24">
        <div className="container-npbc">
          <div data-reveal>
            <SectionHeader eyebrow="The Team" title="Meet our leadership." subtitle="Together, we are stewards of the vision God has given to New Philadelphia." />
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {leaders.slice(1).map((l, i) => (
              <article
                key={l.name}
                data-reveal
                data-testid={`leader-card-${i}`}
                style={{ transitionDelay: `${i * 50}ms` }}
                className="group card-elevated overflow-hidden hover:-translate-y-1 transition-all duration-500"
              >
                <div className="relative h-72 overflow-hidden">
                  <img src={l.image} alt={l.name} className="h-full w-full object-cover transition-transform duration-[1400ms] group-hover:scale-105" loading="lazy" />
                  <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/70 to-transparent" />
                </div>
                <div className="p-6">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-npbc-red">{l.role}</div>
                  <h3 className="mt-2 heading-serif text-2xl">{l.name}</h3>
                  <p className="mt-2 text-sm text-npbc-gray/85 leading-relaxed line-clamp-4">{l.bio}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
      <StockPhotoNotice />
    </div>
  );
}
